import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Edit, Ellipsis, Link2, MessageCircle, Star } from "lucide-react";

import { AppStateContext } from '@/state/AppStateContext';
import { addAgentUser, getAgentDetails } from "@/api/api";
import { Agent } from '@/api/models';
import { changeAgent, refreshUserAgents } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface AgentDetailsProps {
  isViewDialogOpen: boolean;
  setIsViewDialogOpen: (isOpen: boolean) => void;
  selectedAgent: Agent | null;
  editable: boolean;
}

const AgentDetails = ({ isViewDialogOpen, setIsViewDialogOpen, selectedAgent, editable }: AgentDetailsProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const handleRefreshUserAgents = () => refreshUserAgents(appStateContext);
  const handleAgentChange = (agent: Agent) => changeAgent(appStateContext, agent);
  const [loading, setLoading] = useState<boolean>(false);
  const [copyLinkText, setCopyLinkText] = useState<string>('Copy Link');
  const [customGPTArr, setCustomGPTArr] = useState<any>([]);
  const [customGPTsExpanded, setCustomGPTsExpanded] = useState<boolean>(false);
  const [startingConversation, setStartingConversation] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  
  /** 
   * Handle loading additional agent details
  **/
  useEffect(() => {
    setCustomGPTArr([]);
    if (selectedAgent) handleGetAgentDetails();
  }, [selectedAgent]);
  
  /** 
   * Handle getting details for the selected agent
  **/
  const handleGetAgentDetails = () => {
    setLoading(true);
    getAgentDetails(selectedAgent?.id || '').then((res) => {
      // Set Custom GPTs array
      if (res && res.customGPTs && res.customGPTs.length > 0) setCustomGPTArr(res.customGPTs);
      else setCustomGPTArr([]);

      setLoading(false);
    }).catch((err) => {
      console.error('Error fetching details:', err);
      setCustomGPTArr([]);
      setLoading(false);
    });
  }

  /** 
   * Handle starting a conversation with an agent
   * Adding the agent to the user's list and navigating to chat
  **/
  const handleStartConversation = async () => {
    if (selectedAgent) {
      setStartingConversation(true);
      
      // Add selected agent to current user
      await addAgentUser(selectedAgent);

      // Refresh the user's agent list in sidebar
      handleRefreshUserAgents();

      // Navigate back to chat and set selected agent
      setStartingConversation(false);
      navigate('/');
      handleAgentChange(selectedAgent);
      setIsViewDialogOpen(false);
    }
  }

  /** 
   * Handle opening the edit dialog for an agent
  **/
  const onEdit = () => {
    navigate('/agents/e/' + selectedAgent?.id);
  }

  /** 
   * Handle copying the link to the agent
  **/
  const onCopyLink = () => {
    setCopyLinkText('Copied Link');
    navigator.clipboard.writeText(window.location.origin + "/agents/v/" + selectedAgent?.id);
    setTimeout(() => {
      setCopyLinkText('Copy Link');
      setIsPopoverOpen(false);
    }, 3000);
  }

  return (
    <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
      <DialogContent className="flex flex-col max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-1/2 bg-secondary border-transparent rounded-3xl shadow-short p-0" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>
  
        <div className="absolute top-4 right-12">
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger>
              <Ellipsis size={18} />
            </PopoverTrigger>
            <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[175px] p-1 me-12">
              <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3" onClick={onCopyLink}>
                <Link2 className="" size={16} />
                <div className="select-none text-sm">{copyLinkText}</div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col max-h-[90vh] overflow-x-hidden scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 px-4 mb-2">
          <div className="flex-1">
            <div className="flex flex-col items-center">
              <h1 className="text-3xl dark:text-white font-bold mt-4">{selectedAgent?.name || ''}</h1>
              <h6 className="text-sm mt-2 mb-3">By: <strong>{selectedAgent?.created_by_name || ''}</strong></h6>
              <h6 className="text-center text-sm">{selectedAgent?.description || ''}</h6>

              <div className="flex w-full mt-4 mb-2">
                <div className="flex flex-grow items-center justify-center w-1/4">
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1">
                      <Star size={20} className="text-accent" fill="currentColor" />
                      <span className="font-bold">Spaces Selector</span>
                    </div>
                    <div className="text-sm">Type</div>
                  </div>
                </div>
              </div>

              {customGPTArr.length > 0 ?
                <div className="w-full mt-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setCustomGPTsExpanded(!customGPTsExpanded)}>{customGPTsExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />} Spaces</button>
                  <div className={`transition-all duration-300 overflow-hidden ${customGPTsExpanded ? 'opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                    <div className="flex flex-grow flex-wrap gap-2">
                      {customGPTArr.map((cgpt: any, idx: number) => 
                        <div key={idx} className="border border-accent bg-neutral rounded-lg text-xs px-3 py-1">{cgpt}</div>
                      )}
                   </div>
                  </div>
                </div>
              : null}
            </div>
          </div>
          
          <div className="flex flex-grow items-center justify-center w-full gap-4 mt-6 mb-2">
            {loading ?
              <span className="loading loading-ring text-primary"></span>
            :
              <>
                <button type="button" className="btn btn-primary flex-grow" onClick={handleStartConversation} disabled={startingConversation}>{startingConversation ? <span className="loading loading-dots loading-lg text-primary"></span> : <><MessageCircle size={20} /> Start Conversation</>}</button>
                
                {(editable && selectedAgent?.created_by == appStateContext?.state.currentUser?.id) ? 
                  <button type="button" className="btn btn-success" onClick={onEdit}><Edit size={20} /> Edit</button>
                : null}
              </>
            }
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

export default AgentDetails;